import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useConnection, useDisconnect } from 'wagmi'
import type { Me } from '@algoboo/schema/api'
import { Button, Icon, SoftAgent, cx, shortAddress } from '@algoboo/ui'
import { endSession, resetSession, useMe } from '../lib/api'
import { env } from '../env'
import { SiweDialog } from './WalletDialog'
import { UserAvatar } from './UserAvatar'
import { AccountMenuItems } from './AccountMenu'

export { SiweDialog }

interface SessionCtx {
  me: Me | null
  loading: boolean
  /** open the connect + sign-in dialog; `reason` explains why we ask */
  signIn: (reason?: string) => void
  signOut: () => Promise<void>
}

const Ctx = createContext<SessionCtx | null>(null)

export function useSession(): SessionCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useSession outside SessionProvider')
  return c
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const me = useMe()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<string | undefined>()
  const { disconnectAsync } = useDisconnect()
  const signIn = useCallback((r?: string) => {
    setReason(r)
    setOpen(true)
  }, [])
  const signOut = useCallback(async () => {
    await endSession() // offline: revoked on reconnect, before the viewer is read again (V-R6)
    await resetSession(qc, null)
    await disconnectAsync().catch(() => undefined)
  }, [qc, disconnectAsync])
  const value = useMemo<SessionCtx>(() => ({ me: me.data ?? null, loading: me.isLoading, signIn, signOut }), [me.data, me.isLoading, signIn, signOut])
  useSessionWalletGuard(me.data ?? null, qc, signIn)
  return (
    <Ctx.Provider value={value}>
      {children}
      <SiweDialog open={open} onClose={() => setOpen(false)} reason={reason} />
    </Ctx.Provider>
  )
}

/**
 * The session belongs to one address on one network. When the wallet switches account (any time it reports
 * another address than the session's) or moves to another chain mid-session, the session ends server-side and
 * the sign-in dialog opens again, so nothing is ever signed or sent from a wallet the session does not match.
 */
export function sessionWalletMismatch(p: { sessionAddress: string | null; walletAddress: string | undefined; status: string; prevChainId: number | undefined; chainId: number | undefined; expectedChainId: number }): 'account' | 'chain' | null {
  if (!p.sessionAddress || p.status !== 'connected' || !p.walletAddress) return null
  if (p.walletAddress.toLowerCase() !== p.sessionAddress.toLowerCase()) return 'account'
  if (p.prevChainId !== undefined && p.chainId !== undefined && p.prevChainId !== p.chainId && p.chainId !== p.expectedChainId) return 'chain'
  return null
}

function useSessionWalletGuard(me: Me | null, qc: ReturnType<typeof useQueryClient>, signIn: (reason?: string) => void) {
  const conn = useConnection()
  const prevChain = useRef<number | undefined>(undefined)
  const handling = useRef(false)
  useEffect(() => {
    const status = conn.status
    const mismatch = sessionWalletMismatch({ sessionAddress: me?.user.primaryAddress ?? null, walletAddress: conn.address, status, prevChainId: prevChain.current, chainId: conn.chainId, expectedChainId: env.chainId })
    if (status === 'connected') prevChain.current = conn.chainId
    if (!mismatch || handling.current) return
    handling.current = true
    const reason =
      mismatch === 'account'
        ? `Your wallet switched to ${shortAddress(conn.address!)}. You were signed out; sign in again with this account.`
        : 'Your wallet moved to another network. You were signed out; switch back and sign in again.'
    void endSession()
      .then(() => resetSession(qc, null))
      .finally(() => {
        handling.current = false
        signIn(reason)
      })
  }, [me, conn.address, conn.chainId, conn.status, qc, signIn])
}

/** Header wallet control: connect, sign in, or the signed-in menu. */
export function WalletButton() {
  const { me, signIn, signOut } = useSession()
  const conn = useConnection()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!me) {
    return (
      <Button size="sm" variant="secondary" icon="wallet" onClick={() => signIn()} data-testid="wallet-button" data-state={conn.isConnected ? 'connected' : 'disconnected'}>
        {conn.isConnected && conn.address ? (<>Sign in<span className="max-sm:hidden">{` · ${shortAddress(conn.address, 4, 3)}`}</span></>) : 'Connect'}
      </Button>
    )
  }
  const addr = me.user.primaryAddress
  return (
    <div ref={ref} className="relative flex min-w-0">
      <button
        type="button"
        data-testid="wallet-button"
        data-state="signed-in"
        data-address={addr}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="rh-hit flex h-8 min-w-0 max-w-[44vw] items-center gap-2 rounded-[8px] border border-line-strong bg-surface pl-1 pr-2.5 hover:border-field"
      >
        <UserAvatar user={me.user} size={24} />
        <span className="truncate text-[13px] font-medium text-ink">{me.user.handle ? `@${me.user.handle}` : shortAddress(addr, 4, 3)}</span>
        <Icon name="chevron-down" size={14} className="shrink-0 text-ink-3" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-10 z-40 max-h-[calc(100dvh-72px)] w-[min(15rem,calc(100vw-2rem))] overflow-y-auto overscroll-contain rounded-lg border border-line bg-surface p-1 shadow-float">
          <div className="px-3 py-2">
            <div className="text-[13px] font-semibold text-ink">{me.user.displayName}</div>
            <div className="font-mono text-[11.5px] text-ink-3">{shortAddress(addr, 8, 6)}</div>
          </div>
          <div className="my-1 h-px bg-line" />
          <AccountMenuItems signedIn isAdmin={!!me.isAdmin} onClose={() => setOpen(false)} />
          <div className="my-1 h-px bg-line" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              void signOut()
            }}
            className="flex min-h-11 w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[13.5px] text-ink-2 hover:bg-sunken hover:text-ink md:min-h-0"
          >
            <Icon name="logout" size={15} /> Sign out
          </button>
        </div>
      )}
    </div>
  )
}

/** Card shown in place of owner/customer content when signed out. */
export function SignInPrompt({ title, body, className }: { title: string; body: string; className?: string }) {
  const { signIn } = useSession()
  return (
    <div className={cx('flex flex-col items-center gap-4 rounded-xl border border-line bg-surface px-6 py-10 text-center', className)}>
      <SoftAgent avatar={{ family: 'lumi', palette: 'mint', expression: 'curious', headwear: 'beanie' }} size={72} framing="full" title="" />
      <div className="max-w-md">
        <h2 className="font-display text-[18px] font-semibold text-ink">{title}</h2>
        <p className="mt-1.5 text-[14px] text-ink-2">{body}</p>
      </div>
      <Button variant="primary" icon="wallet" onClick={() => signIn(body)}>
        Connect wallet
      </Button>
    </div>
  )
}

/** Gate an action: runs `fn` when signed in, else opens the sign-in dialog with `reason`. */
export function useRequireSession() {
  const { me, signIn } = useSession()
  return useCallback(
    (reason: string, fn: () => void) => {
      if (me) fn()
      else signIn(reason)
    },
    [me, signIn],
  )
}
